import { ClientBankAccountsLists, PaasTransaction } from 'vopay';
import { useEffect, useRef, useState } from 'react';
import { useApi } from '#shared/providers/Api';
import useEnrichPaymentMethodData from '#shared/hooks/useApi/useEnrichPaymentMethodData';
import useFetchEnrichmentStatus from '#shared/hooks/useApi/useFetchEnrichmentStatus';
import useFetchEnrichedReports, { EnrichedReports } from '#shared/hooks/useApi/useFetchEnrichedReports';
import { ALL_CATEGORIES, CategoryDescriptor, toCategoryDescriptor } from './categories';

export type EnrichmentTier = 'basic' | 'enhanced';

export type EnrichmentStage = 'locked' | 'enriching' | 'loadingReports' | 'ready' | 'error';

export type EnrichmentProgress = {
  numEnriched: number;
  numTotal: number;
};

// The backend only enriches tokens backed by a flinks or fiserv aggregator connection
export function isEnrichmentEligible(account: ClientBankAccountsLists): boolean {
  return Boolean(account.FlinksAccountID || account.FlinksLoginID || account.FiservToken);
}

const POLL_INTERVAL_MS = 3000;
// Stop polling after 5 minutes rather than spinning forever on a stuck queue
const MAX_POLLS = 100;

// The signed-in portal user is a host fact in shared — passed in instead of the
// portal's ClientData provider.
export default function useDataEnrichment(token: string, loginUserName = '') {
  const api = useApi();
  const portalUser = loginUserName;

  const [stage, setStage] = useState<EnrichmentStage>('locked');
  const [tier, setTier] = useState<EnrichmentTier>('basic');
  const [progress, setProgress] = useState<EnrichmentProgress>({ numEnriched: 0, numTotal: 0 });
  const [reports, setReports] = useState<EnrichedReports | null>(null);
  const [transactions, setTransactions] = useState<PaasTransaction[]>([]);
  // Taxonomy from the backend, with the static list as the offline fallback
  const [categories, setCategories] = useState<CategoryDescriptor[]>(ALL_CATEGORIES);

  const { enrichPaymentMethodData } = useEnrichPaymentMethodData();
  const { fetchEnrichmentStatus } = useFetchEnrichmentStatus();
  const { fetchEnrichedReports } = useFetchEnrichedReports();

  // Bumped on token change/unmount so an in-flight poll loop stops writing state
  const runIdRef = useRef(0);

  useEffect(() => {
    runIdRef.current += 1;
    setStage('locked');
    setTier('basic');
    setProgress({ numEnriched: 0, numTotal: 0 });
    setReports(null);
    setTransactions([]);

    return () => {
      runIdRef.current += 1;
    };
  }, [token]);

  // The static fallback list keeps the feature working when the categories
  // endpoint is unavailable, so a failure here is deliberately non-fatal
  async function loadCategories(): Promise<CategoryDescriptor[]> {
    try {
      const res = await api.fetchClientEnrichmentCategories({ PortalUser: portalUser });
      if (!res.Success || res.Categories.length === 0) return ALL_CATEGORIES;

      const loaded = res.Categories.map(toCategoryDescriptor);
      setCategories(loaded);

      return loaded;
    } catch (error) {
      console.error(error);

      return ALL_CATEGORIES;
    }
  }

  async function fetchTransactions(): Promise<PaasTransaction[]> {
    const res = await api.fetchClientTransactions({ PortalUser: portalUser, Token: token });
    if (!res.Success) throw new Error(res.ErrorMessage);

    return res.AggregatorTransactions;
  }

  async function loadReports(
    runId: number,
    selectedTier: EnrichmentTier,
    categoryList: CategoryDescriptor[],
    prefetchedTransactions?: PaasTransaction[],
  ) {
    setStage('loadingReports');

    const [fetchedReports, loadedTransactions] = await Promise.all([
      fetchEnrichedReports(
        { PortalUser: portalUser, Token: token },
        {
          isEnhanced: selectedTier === 'enhanced',
          reportTypes: categoryList.map((category) => category.reportType),
        },
      ),
      prefetchedTransactions ? Promise.resolve(prefetchedTransactions) : fetchTransactions(),
    ]);

    if (runIdRef.current !== runId) return;

    setReports(fetchedReports);
    setTransactions(loadedTransactions);
    setStage('ready');
  }

  async function pollUntilComplete(runId: number, requestID: string) {
    for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
      if (runIdRef.current !== runId) return;

      const status = await fetchEnrichmentStatus({ PortalUser: portalUser, RequestID: requestID });

      if (runIdRef.current !== runId) return;

      setProgress({ numEnriched: status.EnrichedCount, numTotal: status.TotalCount });

      if (status.Status === 'Complete') return;

      await new Promise((resolve) => {
        setTimeout(resolve, POLL_INTERVAL_MS);
      });
    }

    throw new Error('Data enrichment timed out');
  }

  // Reclassify one transaction, then silently reload reports and transactions
  // so the charts reflect the correction without leaving the insights view
  async function updateTransactionCategory(transactionID: string, classification: string) {
    const runId = runIdRef.current;

    const res = await api.updateClientTransaction({
      PortalUser: portalUser,
      Token: token,
      TransactionID: transactionID,
      Classification: classification,
    });

    if (!res.Success) throw new Error(res.ErrorMessage);

    const [fetchedReports, refreshedTransactions] = await Promise.all([
      fetchEnrichedReports(
        { PortalUser: portalUser, Token: token },
        { isEnhanced: tier === 'enhanced', reportTypes: categories.map((category) => category.reportType) },
      ),
      fetchTransactions(),
    ]);

    if (runIdRef.current !== runId) return;

    setReports(fetchedReports);
    setTransactions(refreshedTransactions);
  }

  async function unlock(selectedTier: EnrichmentTier) {
    const runId = runIdRef.current;
    setTier(selectedTier);

    try {
      // Check the enrichment state first: when the token was enriched before,
      // insights load straight away with no enrichment run.
      setStage('loadingReports');
      const [categoryList, existingTransactions] = await Promise.all([loadCategories(), fetchTransactions()]);

      if (runIdRef.current !== runId) return;

      const isAlreadyEnriched =
        existingTransactions.length > 0 && existingTransactions.every((transaction) => transaction.IsDataEnriched);

      if (isAlreadyEnriched) {
        await loadReports(runId, selectedTier, categoryList, existingTransactions);

        return;
      }

      setStage('enriching');

      setProgress({
        numEnriched: existingTransactions.filter((transaction) => transaction.IsDataEnriched).length,
        numTotal: existingTransactions.length,
      });

      const res = await enrichPaymentMethodData({ PortalUser: portalUser, Token: token });
      if (runIdRef.current !== runId) return;

      await pollUntilComplete(runId, res.RequestID);
      if (runIdRef.current !== runId) return;

      // Rows changed during enrichment (classifications were written) — refetch
      await loadReports(runId, selectedTier, categoryList);
    } catch (error) {
      console.error(error);
      if (runIdRef.current === runId) setStage('error');
    }
  }

  return {
    stage,
    tier,
    progress,
    reports,
    transactions,
    categories,
    unlock,
    updateTransactionCategory,
  };
}
